import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ClipboardList, FileText, Shield, ShieldCheck, TrendingUp, AlertCircle, RefreshCw, X, BarChart2, DollarSign, FileSpreadsheet, FileDown, Calculator, CalendarDays } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { exportPendingExcel } from '../../utils/exportPendingExcel';
import { exportPendingPdf } from '../../utils/exportPendingPdf';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const TABS = [
  { id: 'stats',      labelKey: 'statistics', icon: BarChart2 },
  { id: 'finance',    labelKey: 'finance',    icon: DollarSign },
  { id: 'accounting', labelKey: 'accounting', icon: Calculator },
];

function fmt(v) {
  return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function AmountLabel({ gel, amd }) {
  const parts = [];
  if (gel > 0) parts.push(`${fmt(gel)} GEL`);
  if (amd > 0) parts.push(`${fmt(amd)} AMD`);
  return <span>{parts.length ? parts.join(' / ') : '—'}</span>;
}

function StatCard({ icon: Icon, label, value, sub, color = 'blue', onClick }) {
  const colors = {
    blue:   'bg-primary-100 text-primary-600',
    green:  'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red:    'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  return (
    <div
      className={`bg-white p-4 rounded-lg shadow-sm transition-shadow hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-800">{value ?? '—'}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-3 rounded-full ${colors[color]}`}>
          <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
      </div>
    </div>
  );
}

const CHART_PERIODS = [
  { id: 'day',     labelKey: 'periodDay' },
  { id: 'week',    labelKey: 'periodWeek' },
  { id: 'month',   labelKey: 'periodMonth' },
  { id: '90days',  labelKey: 'period90days' },
  { id: '6months', labelKey: 'period6months' },
  { id: 'year',    labelKey: 'periodYear' },
];

function MiniBar({ counts, days }) {
  if (!counts || counts.length === 0) return null;
  const max = Math.max(...counts, 1);
  // Thin out labels when there are many bars so they don't overlap.
  const step = counts.length > 14 ? Math.ceil(counts.length / 12) : 1;
  return (
    <div className="flex items-end gap-1 h-24">
      {counts.map((c, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <div
            className="w-full bg-primary-500 rounded-t"
            style={{ height: `${Math.round((c / max) * 68)}px`, minHeight: c > 0 ? 4 : 0 }}
            title={`${days[i]}: ${c}`}
          />
          <span className="text-[10px] text-gray-400 truncate w-full text-center">
            {i % step === 0 ? days[i] : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

// Payable currency of an invoice: GEL only for Georgia NON-converted invoices;
// AMD otherwise (incl. Georgia russiaInsurance, which converts). Mirrors backend.
function invCurrency(inv) {
  return (inv.tts_georgia && inv.service_type !== 'russiaInsurance') ? 'gel' : 'amd';
}

function groupByUser(list) {
  const map = {};
  for (const inv of list) {
    const key = inv.user || '—';
    if (!map[key]) map[key] = { invoices: [], userId: inv.user_id ?? inv.created_by, gel: 0, amd: 0 };
    map[key].invoices.push(inv);
    const cur = invCurrency(inv);
    const amount = inv.remaining !== undefined ? inv.remaining : (cur === 'gel' ? inv.total_amountGel : inv.total_amount);
    if (cur === 'gel') map[key].gel += amount;
    else map[key].amd += amount;
  }
  return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
}

function InvoiceRow({ inv }) {
  const { t } = useLanguage();
  return (
    <tr className="bg-white border-b border-gray-100 last:border-0">
      <td className="pl-10 pr-4 py-2 text-gray-400 text-xs">{inv.invoice_number || '—'}</td>
      <td className="px-4 py-2 text-gray-700">{inv.company}</td>
      <td className="px-4 py-2 text-gray-600 text-xs">{t(inv.service_type)}</td>
      <td className="px-4 py-2 text-right text-gray-600">{inv.quantity}</td>
      <td className="px-4 py-2 text-right">
        {invCurrency(inv) === 'gel' ? (
          <span className="inline-block bg-green-100 text-green-700 rounded px-2 py-0.5 text-xs font-semibold">
            {fmt(inv.total_amountGel)} GEL
            {inv.paid_amount > 0 && <span className="ml-1 font-normal text-green-500">→ {fmt(inv.remaining)} {t('leftSuffix')}</span>}
          </span>
        ) : (
          <span className="inline-block bg-blue-100 text-blue-700 rounded px-2 py-0.5 text-xs font-semibold">
            {fmt(inv.total_amount)} AMD
            {inv.paid_amount > 0 && <span className="ml-1 font-normal text-blue-400">→ {fmt(inv.remaining)} {t('leftSuffix')}</span>}
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-gray-400 text-xs">{inv.date}</td>
    </tr>
  );
}

function UserGroup({ user, gel, amd, invoices, showExport, distributeMode, onProcessed }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState(null); // 'excel' | 'pdf' | null
  const [distGel, setDistGel] = useState('');
  const [distAmd, setDistAmd] = useState('');
  const [distSaving, setDistSaving] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);   // extended monthly export modal
  const [months, setMonths] = useState([]);
  const [selMonth, setSelMonth] = useState('');
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyBusy, setMonthlyBusy] = useState(false);

  const userId = invoices[0]?.user_id;

  const distribute = async (currency) => {
    const amount = parseFloat(currency === 'gel' ? distGel : distAmd);
    if (!(amount > 0) || !userId) return;
    setDistSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/invoices/distribute-payment/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, currency, amount }),
      });
      if (res.ok) {
        if (currency === 'gel') setDistGel(''); else setDistAmd('');
        if (onProcessed) onProcessed();
      }
    } catch {}
    finally { setDistSaving(false); }
  };

  const openExport = (fmt) => (e) => { e.stopPropagation(); setExportFormat(fmt); };

  // ── Extended monthly export (Shablon 3-sheet) ──────────────────────────────
  const openMonthly = (e) => {
    e.stopPropagation();
    if (!userId) return;
    setMonthlyOpen(true); setMonths([]); setSelMonth(''); setMonthlyLoading(true);
    const token = localStorage.getItem('auth_token');
    fetch(`${BASE_URL}/invoices/monthly-export-availability/?user_id=${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : { months: [] }))
      .then(d => setMonths(d.months || []))
      .catch(() => setMonths([]))
      .finally(() => setMonthlyLoading(false));
  };

  const selMonthObj = months.find(mo => mo.label === selMonth);

  const runMonthly = async (mode) => {
    if (!selMonth) return;
    const [y, m] = selMonth.split('-').map(Number);
    setMonthlyBusy(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/invoices/monthly-export/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, year: y, month: m, mode }),
      });
      if (!res.ok) {
        let msg = t('exportFailed');
        try { const j = await res.json(); msg = j.error || j.reason || msg; } catch {}
        alert(msg); return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const mt = /filename="?([^"]+)"?/.exec(cd);
      const filename = mt ? mt[1] : `export_${selMonth}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      if (mode === 'accounting') { setMonthlyOpen(false); if (onProcessed) onProcessed(); }
    } catch { alert(t('exportFailed')); }
    finally { setMonthlyBusy(false); }
  };

  const doExport = () => {
    const company = (invoices[0] && invoices[0].company) || user;
    if (exportFormat === 'excel') exportPendingExcel(company, invoices);
    else if (exportFormat === 'pdf') exportPendingPdf(company, invoices);
    setExportFormat(null);
  };

  const doExportAccounting = async () => {
    // 1) Produce the export file (same as a normal export). The util also returns
    //    the file as a Blob so we can attach it to the firm's document storage.
    const company = (invoices[0] && invoices[0].company) || user;
    let exported = null;
    if (exportFormat === 'excel') exported = exportPendingExcel(company, invoices);
    else if (exportFormat === 'pdf') exported = await exportPendingPdf(company, invoices);

    const token = localStorage.getItem('auth_token');

    // 2) Attach the exported document to the matching firm (GEL → TTS Georgia LLC).
    if (exported && exported.blob) {
      try {
        const fd = new FormData();
        fd.append('file', exported.blob, exported.filename);
        fd.append('is_gel', exported.isGel ? 'true' : 'false');
        await fetch(`${BASE_URL}/accounting/attach-export/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
      } catch {}
    }

    // 3) Move all these pending invoices to 'processing'.
    try {
      await fetch(`${BASE_URL}/invoices/mark-processing/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: invoices.map(i => i.id) }),
      });
    } catch {}
    setExportFormat(null);
    if (onProcessed) onProcessed(); // refresh dashboard + close list modal
  };

  return (
    <>
      <tr
        className="bg-gray-50 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => setOpen(o => !o)}
      >
        <td className="px-4 py-3" colSpan={2}>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-bold text-sm w-4 text-center">{open ? '−' : '+'}</span>
            <span className="font-semibold text-gray-800">{user}</span>
            <span className="text-xs text-gray-400">({invoices.length})</span>
          </div>
        </td>
        <td className="px-4 py-3" />
        <td className="px-4 py-3" />
        <td className="px-4 py-3 text-right">
          {gel > 0 && <span className="inline-block bg-green-100 text-green-700 rounded px-2 py-0.5 text-xs font-bold mr-1">{fmt(gel)} GEL</span>}
          {amd > 0 && <span className="inline-block bg-blue-100 text-blue-700 rounded px-2 py-0.5 text-xs font-bold">{fmt(amd)} AMD</span>}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap" onClick={e => distributeMode && e.stopPropagation()}>
          {distributeMode && (
            <div className="inline-flex flex-col items-end gap-1">
              {gel > 0 && (
                <div className="flex items-center gap-1">
                  <input type="number" min="0" step="0.01" value={distGel} disabled={distSaving}
                    onChange={e => setDistGel(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') distribute('gel'); }}
                    placeholder="GEL"
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-green-500" />
                  <button onClick={() => distribute('gel')} disabled={distSaving || !(parseFloat(distGel) > 0)}
                    className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-40 whitespace-nowrap">
                    {t('distribute') || 'Distribute'} GEL
                  </button>
                </div>
              )}
              {amd > 0 && (
                <div className="flex items-center gap-1">
                  <input type="number" min="0" step="0.01" value={distAmd} disabled={distSaving}
                    onChange={e => setDistAmd(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') distribute('amd'); }}
                    placeholder="AMD"
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  <button onClick={() => distribute('amd')} disabled={distSaving || !(parseFloat(distAmd) > 0)}
                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-40 whitespace-nowrap">
                    {t('distribute') || 'Distribute'} AMD
                  </button>
                </div>
              )}
            </div>
          )}
          {showExport && (
            <span className="inline-flex items-center gap-1.5">
              <button
                onClick={openExport('excel')}
                title={t('exportToExcel')}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-xs font-medium"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                Excel
              </button>
              <button
                onClick={openExport('pdf')}
                title={t('exportToPdf')}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
              >
                <FileDown className="h-3.5 w-3.5" />
                PDF
              </button>
              <button
                onClick={openMonthly}
                title={t('extendedExport')}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-xs font-medium"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                {t('monthly')}
              </button>
            </span>
          )}
        </td>
      </tr>
      {open && invoices.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}

      {exportFormat && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setExportFormat(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
            <h3 className="text-base font-semibold text-gray-800 mb-1">
              {exportFormat === 'excel' ? 'Excel' : 'PDF'} — {user}
            </h3>
            <p className="text-xs text-gray-500 mb-4">{t('chooseExportType')}</p>
            <div className="flex flex-col gap-2">
              <button onClick={doExport}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium">
                {t('export')}
              </button>
              <button onClick={doExportAccounting}
                className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium">
                {t('exportToAccounting')}
              </button>
              <button onClick={() => setExportFormat(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm">
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {monthlyOpen && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setMonthlyOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-5">
            <h3 className="text-base font-semibold text-gray-800 mb-1">{t('extendedExport')} — {user}</h3>
            <p className="text-xs text-gray-500 mb-4">{t('extendedExportDesc')}</p>
            {monthlyLoading ? (
              <div className="py-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" /> {t('loadingEllipsis')}
              </div>
            ) : months.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-400">{t('noAvailableMonths')}</p>
            ) : (
              <>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t('selectMonth')}</label>
                <select value={selMonth} onChange={e => setSelMonth(e.target.value)}
                  className="w-full mb-3 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">{t('selectPlaceholder')}</option>
                  {months.map(mo => (
                    <option key={mo.label} value={mo.label} disabled={!mo.has_data}>
                      {mo.label}{mo.has_data ? '' : ` (${t('noDataMonth')})`}
                    </option>
                  ))}
                </select>
                {selMonthObj && !selMonthObj.accounting_enabled && (
                  <p className="text-xs text-amber-600 mb-3">
                    {selMonthObj.accounting_reason === 'already_exported'
                      ? t('accUnavailAlready')
                      : t('accUnavailPartly')}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <button onClick={() => runMonthly('simple')} disabled={!selMonth || monthlyBusy}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium disabled:opacity-50">
                    {monthlyBusy ? '…' : t('export')}
                  </button>
                  <button onClick={() => runMonthly('accounting')}
                    disabled={!selMonth || monthlyBusy || !selMonthObj?.accounting_enabled}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                    {t('exportToAccounting')}
                  </button>
                  <button onClick={() => setMonthlyOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm">
                    {t('cancel')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function InvoiceModal({ kind, list, onClose, onProcessed }) {
  const { t } = useLanguage();
  if (!list) return null;
  const groups = groupByUser(list);
  const isPending = kind === 'pending';
  const isProcessing = kind === 'processing';
  const title = isPending ? t('pendingInvoices') : t('processingInvoices');

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{title} ({list.length})</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-white sticky top-0 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold" colSpan={2}>{t('userCompany')}</th>
                <th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{t('service')}</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500 font-semibold">{t('qty')}</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500 font-semibold">{t('amount')}</th>
                <th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{t('date')}</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([user, { gel, amd, invoices }]) => (
                <UserGroup key={user} user={user} gel={gel} amd={amd} invoices={invoices} showExport={isPending} distributeMode={isProcessing} onProcessed={onProcessed} />
              ))}
              {groups.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">{t('noInvoices')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TabStats({ stats, userId }) {
  const { t } = useLanguage();
  const [period, setPeriod] = useState(stats.chart_period || 'week');
  const [chart, setChart] = useState(stats.chart);
  const [chartLoading, setChartLoading] = useState(false);

  // Resync when the underlying stats reload (e.g. user filter changed).
  useEffect(() => {
    setChart(stats.chart);
    setPeriod(stats.chart_period || 'week');
  }, [stats]);

  const changePeriod = async (p) => {
    setPeriod(p);
    setChartLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${BASE_URL}/auth/admin-chart/?period=${p}${userId ? `&user_id=${userId}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setChart(d.chart); }
    } catch {}
    finally { setChartLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={ClipboardList} label={t('registrations')}      value={stats.registrations.total}          color="blue"   />
        <StatCard icon={ClipboardList} label={t('onRoad')}             value={stats.registrations.on_road}         color="green"  />
        <StatCard icon={ClipboardList} label={t('completedToday')}     value={stats.registrations.completed_today} color="green"  />
        <StatCard icon={FileText}      label={t('invoices')}            value={stats.invoices.total}                color="blue"   />
        <StatCard icon={FileText}      label={t('pending')}            value={stats.invoices.pending}              color="yellow" />
        <StatCard icon={FileText}      label={t('statusProcessing')}   value={stats.invoices.processing}           color="blue"   />
        <StatCard icon={FileText}      label={t('paid')}               value={stats.invoices.paid}                 color="green"  />
        <StatCard icon={Shield}        label={t('insurance')}           value={stats.insurance.total}               color="blue"   />
        <StatCard icon={Shield}        label={t('expiring10d')}        value={stats.insurance.expiring_soon}       sub={`${t('expiredLabel')}: ${stats.insurance.expired}`} color="red" />
        <StatCard icon={ShieldCheck}   label={t('orderedInsurances')}   value={stats.preorders.total}               color="blue"   />
        <StatCard icon={ShieldCheck}   label={t('unpaidPreorders')}    value={stats.preorders.unpaid}              color="yellow" />
        <StatCard icon={ShieldCheck}   label={t('unsignedPreorders')}  value={stats.preorders.unsigned}            color="red"    />
      </div>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary-500" />
            <h2 className="text-sm font-semibold text-gray-800">
              {t('registrations')} — {t(CHART_PERIODS.find(p => p.id === period)?.labelKey || 'periodWeek')}
            </h2>
          </div>
          <select
            value={period}
            onChange={e => changePeriod(e.target.value)}
            disabled={chartLoading}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {CHART_PERIODS.map(p => (
              <option key={p.id} value={p.id}>{t(p.labelKey)}</option>
            ))}
          </select>
        </div>
        <div className={chartLoading ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
          <MiniBar counts={chart.counts} days={chart.days} />
        </div>
      </div>
    </div>
  );
}

function AllInvoicesModal({ detail, onClose }) {
  const { t } = useLanguage();
  if (!detail) return null;
  const { amd, gel } = detail;
  const Row = ({ label, value, currency, bold, color }) => (
    <div className={`flex justify-between items-center py-2 border-b border-gray-50 last:border-0 ${bold ? 'font-semibold' : ''}`}>
      <span className={`text-sm ${color || 'text-gray-600'}`}>{label}</span>
      <span className={`text-sm ${color || 'text-gray-800'}`}>{fmt(value)} {currency}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{t('allInvoicesBreakdown')}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {amd.total > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase font-semibold mb-2">{t('amdInvoices')}</p>
              <div className="bg-gray-50 rounded-lg px-4 py-1">
                <Row label={t('totalAmdNoVat')}   value={amd.no_vat}     currency="AMD" />
                <Row label={t('commission')}       value={amd.commission} currency="AMD" color="text-yellow-600" />
                <Row label={t('vat20')}            value={amd.vat}        currency="AMD" color="text-red-500" />
                <Row label={t('totalAmdWithVat')}  value={amd.total}      currency="AMD" bold color="text-blue-700" />
              </div>
            </div>
          )}
          {gel.total > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase font-semibold mb-2">{t('gelInvoicesTts')}</p>
              <div className="bg-gray-50 rounded-lg px-4 py-1">
                <Row label={t('netAmountNoCommission')}  value={gel.subtotal}   currency="GEL" />
                <Row label={t('commission')}              value={gel.commission} currency="GEL" color="text-yellow-600" />
                <Row label={t('totalGelLabel')}           value={gel.total}      currency="GEL" bold color="text-green-700" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdvanceModal({ list, onClose }) {
  const { t } = useLanguage();
  const rows = list || [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-800">{t('userAdvances')} ({rows.length})</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-white sticky top-0 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{t('user')}</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500 font-semibold">GEL</th>
                <th className="text-right px-4 py-2 text-xs text-gray-500 font-semibold">AMD</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.user_id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-2 font-medium text-gray-800">{r.user || '—'}</td>
                  <td className="px-4 py-2 text-right">
                    {r.gel > 0
                      ? <span className="inline-block bg-green-100 text-green-700 rounded px-2 py-0.5 text-xs font-semibold">{fmt(r.gel)} GEL</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {r.amd > 0
                      ? <span className="inline-block bg-blue-100 text-blue-700 rounded px-2 py-0.5 text-xs font-semibold">{fmt(r.amd)} AMD</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">{t('noAdvances')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TabFinance({ stats, onOpenModal, fullAdmin }) {
  const { t } = useLanguage();
  const tr = stats.transactions;
  const adv = stats.advances || { gel: 0, amd: 0, list: [] };
  const hasAdvance = adv.gel > 0 || adv.amd > 0;
  const [allInvModal, setAllInvModal] = useState(false);
  const [advModal, setAdvModal] = useState(false);
  return (
    <div className="space-y-6">
      {/* Invoice cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div
          className="bg-white rounded-xl shadow-sm border-l-4 border-yellow-400 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onOpenModal({ kind: 'pending' })}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{t('pending')}</p>
            <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">
              {stats.invoices.pending} {t('invoices')}
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            <AmountLabel gel={stats.invoices.pending_gel} amd={stats.invoices.pending_amd} />
          </p>
          <p className="text-xs text-gray-400 mt-2">{t('clickToViewList')}</p>
        </div>

        <div
          className="bg-white rounded-xl shadow-sm border-l-4 border-blue-400 p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => onOpenModal({ kind: 'processing' })}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{t('processingRemainder')}</p>
            <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">
              {stats.invoices.processing} {t('invoices')}
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            <AmountLabel gel={stats.invoices.processing_gel} amd={stats.invoices.processing_amd} />
          </p>
          <p className="text-xs text-gray-400 mt-2">{t('clickToViewList')}</p>
        </div>
      </div>

      {/* Transactions summary — hidden for limited admins */}
      {fullAdmin && (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="h-5 w-5 text-green-500" />
          <h2 className="text-sm font-semibold text-gray-800">
            {t('transactions')}
            <span className="ml-2 text-xs text-gray-400 font-normal">({tr.count} {t('totalLower')})</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* All invoices */}
          <div className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => setAllInvModal(true)}>
            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">{t('allInvoices')}</p>
            {stats.all_invoices_gel > 0 && (
              <p className="text-lg font-bold text-gray-700">{fmt(stats.all_invoices_gel)} GEL</p>
            )}
            {stats.all_invoices_amd > 0 && (
              <p className="text-lg font-bold text-gray-700">{fmt(stats.all_invoices_amd)} AMD</p>
            )}
            {stats.all_invoices_gel === 0 && stats.all_invoices_amd === 0 && <p className="text-gray-400">—</p>}
            <p className="text-xs text-gray-400 mt-1">{t('clickForDetails')}</p>
          </div>

          {/* Payments */}
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-xs text-green-600 uppercase font-semibold mb-2">{t('payments')}</p>
            {tr.pay_gel > 0 && (
              <p className="text-lg font-bold text-green-700">{fmt(tr.pay_gel)} GEL</p>
            )}
            {tr.pay_amd > 0 && (
              <p className="text-lg font-bold text-green-700">{fmt(tr.pay_amd)} AMD</p>
            )}
            {tr.pay_gel === 0 && tr.pay_amd === 0 && <p className="text-gray-400">—</p>}
          </div>

          {/* Unpaid = All - Payments */}
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-xs text-red-500 uppercase font-semibold mb-2">{t('unpaid')}</p>
            {stats.all_invoices_gel > 0 && (
              <p className="text-lg font-bold text-red-600">{fmt(stats.all_invoices_gel - tr.pay_gel)} GEL</p>
            )}
            {stats.all_invoices_amd > 0 && (
              <p className="text-lg font-bold text-red-600">{fmt(stats.all_invoices_amd - tr.pay_amd)} AMD</p>
            )}
            {stats.all_invoices_gel === 0 && stats.all_invoices_amd === 0 && <p className="text-gray-400">—</p>}
          </div>

          {/* Advance (prepaid credit) */}
          <div className={`bg-purple-50 rounded-lg p-4 ${hasAdvance ? 'cursor-pointer hover:bg-purple-100 transition-colors' : ''}`}
            onClick={() => hasAdvance && setAdvModal(true)}>
            <p className="text-xs text-purple-600 uppercase font-semibold mb-2">{t('advance')}</p>
            {adv.gel > 0 && (
              <p className="text-lg font-bold text-purple-700">{fmt(adv.gel)} GEL</p>
            )}
            {adv.amd > 0 && (
              <p className="text-lg font-bold text-purple-700">{fmt(adv.amd)} AMD</p>
            )}
            {!hasAdvance && <p className="text-gray-400">—</p>}
            {hasAdvance && <p className="text-xs text-gray-400 mt-1">{t('clickForUserList')}</p>}
          </div>
        </div>
      </div>
      )}

      {fullAdmin && advModal && (
        <AdvanceModal list={adv.list} onClose={() => setAdvModal(false)} />
      )}

      {fullAdmin && allInvModal && (
        <AllInvoicesModal
          detail={stats.all_invoices_detail}
          onClose={() => setAllInvModal(false)}
        />
      )}
    </div>
  );
}

function TabAccounting({ onDocsChanged }) {
  const { t } = useLanguage();
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendFirm, setSendFirm] = useState(null);  // firm whose send modal is open
  const [selIds, setSelIds] = useState([]);
  const [sending, setSending] = useState(false);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/accounting/firms-documents/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setFirms(d.firms || []); }
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const openSend = (f) => {
    setSendFirm(f);
    // First accountant (the "Бухгалтер") pre-checked by default.
    setSelIds(f.accountants.filter(a => a.is_main).map(a => a.id));
  };
  const toggleId = (id) => setSelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const doSend = async () => {
    if (!sendFirm) return;
    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/accounting/firms/${sendFirm.id}/send-documents/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ accountant_ids: selIds }),
      });
      const r = await res.json().catch(() => ({}));
      if (r.reason === 'no_recipients') alert('Select at least one recipient.');
      else if (r.reason === 'no_documents') alert('No documents to send.');
      else alert(`Sent ${r.sent} document(s) in ${r.batches} email(s). Failed emails: ${r.failed_batches}.` + (r.deleted ? ' Documents removed.' : ''));
      setSendFirm(null);
      fetchDocs();
      onDocsChanged?.();
    } catch { alert('Send failed.'); }
    finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[...Array(2)].map((_, i) => <div key={i} className="bg-white rounded-xl shadow-sm h-48 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {firms.map(f => (
        <div key={f.id} className="bg-white rounded-xl shadow-sm flex flex-col">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-800">{f.name}</h2>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {f.documents.length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto max-h-72 p-3">
            {f.documents.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">{t('noDocuments')}</div>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md">
                {f.documents.map(d => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="text-gray-700 truncate">{d.name}</span>
                    <span className="flex items-center gap-3 whitespace-nowrap">
                      <span className="text-xs text-gray-400">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : ''}</span>
                      {d.file && <a href={d.file} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-600 hover:underline">Open ↗</a>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
            <button
              onClick={() => openSend(f)}
              disabled={f.documents.length === 0}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm"
            >
              {t('send')}
            </button>
          </div>
        </div>
      ))}
      {firms.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">{t('noDocuments')}</div>
      )}

      {sendFirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={e => e.target === e.currentTarget && setSendFirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-800">{t('selectRecipients')}</h2>
                <p className="text-xs text-gray-400">{sendFirm.name}</p>
              </div>
              <button onClick={() => setSendFirm(null)} className="p-1 rounded hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {sendFirm.accountants.length === 0 ? (
                <p className="text-sm text-red-600">No accountants with an email address.</p>
              ) : (
                <ul className="space-y-2">
                  {sendFirm.accountants.map(a => (
                    <li key={a.id}>
                      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={selIds.includes(a.id)} onChange={() => toggleId(a.id)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500" />
                        <span className={a.is_main ? 'font-semibold' : ''}>
                          {a.is_main ? `${t('accountantRole')} — ${a.name}` : a.name}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => setSendFirm(null)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm">{t('cancel')}</button>
              <button onClick={doSend} disabled={sending || selIds.length === 0}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 text-sm">
                {sending ? '…' : t('send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();

  // Full admin = role admin/superadmin + staff + superuser. Limited admins
  // (role=admin, not superuser) don't get the Statistics tab.
  const fullAdmin = !!user && ['admin', 'superadmin'].includes(user.role) && user.is_staff && user.is_superuser;
  const visibleTabs = fullAdmin ? TABS : TABS.filter(x => x.id !== 'stats');

  // Each tab has its own URL: /admin-panel/dashboard/stats | /finance | /accounting.
  const tab = visibleTabs.some(x => x.id === tabParam) ? tabParam : (visibleTabs[0]?.id || 'finance');

  // Limited admin landing on the Statistics URL → send to Finance.
  useEffect(() => {
    if (user && !fullAdmin && tabParam === 'stats') {
      navigate('/admin-panel/dashboard/finance', { replace: true });
    }
  }, [user, fullAdmin, tabParam, navigate]);

  const [stats, setStats]   = useState(null);
  const [users, setUsers]   = useState([]);
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [modal, setModal]   = useState(null);
  const [acctPending, setAcctPending] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/auth/users/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setUsers(await res.json());
    } catch {}
  }, []);

  // Any undelivered accounting invoices (main documents) → yellow dot on the tab.
  const fetchAcctPending = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${BASE_URL}/accounting/firm-counts/`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setAcctPending((d.firms || []).some(f => (f.documents_count || 0) > 0));
      }
    } catch {}
  }, []);

  const fetchStats = useCallback(async (uid) => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const url = uid ? `${BASE_URL}/auth/admin-stats/?user_id=${uid}` : `${BASE_URL}/auth/admin-stats/`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      setStats(await res.json());
    } catch { setError(t('failedLoadStats')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchUsers(); fetchAcctPending(); }, [fetchUsers, fetchAcctPending]);
  useEffect(() => { fetchStats(userId); }, [userId, fetchStats]);

  return (
    <div className="container mx-auto space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{t('dashboard')}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={userId}
            onChange={e => setUserId(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">{t('allUsers')}</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
          <button
            onClick={() => fetchStats(userId)}
            disabled={loading}
            className="px-3 py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-sm"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {t('refresh')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {visibleTabs.map(({ id, labelKey, icon: Icon }) => (
          <button
            key={id}
            onClick={() => navigate(`/admin-panel/dashboard/${id}`)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === id
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(labelKey)}
            {id === 'accounting' && acctPending && (
              <span className="ml-1 h-2 w-2 rounded-full bg-yellow-400" title={t('unsentAccountingInvoices')} />
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-4 h-24 animate-pulse" />
          ))}
        </div>
      ) : stats && (
        <>
          {tab === 'stats'      && <TabStats stats={stats} userId={userId} />}
          {tab === 'finance'    && <TabFinance stats={stats} onOpenModal={setModal} fullAdmin={fullAdmin} />}
          {tab === 'accounting' && <TabAccounting onDocsChanged={fetchAcctPending} />}
        </>
      )}

      {modal && stats && (
        <InvoiceModal
          kind={modal.kind}
          list={modal.kind === 'pending'
            ? stats.invoices.pending_list
            : stats.invoices.processing_list}
          onClose={() => setModal(null)}
          onProcessed={() => fetchStats(userId)}
        />
      )}
    </div>
  );
}
